import Lang from '../../config/lang'
import {
  buildApplicationProgressSteps,
  getApplicationStatusBadge,
  getNormalizedApplicationStatus,
} from '../../config/applicationProgress'

const isValidDate = (value) =>
  value instanceof Date && !Number.isNaN(value.getTime())

const pad2 = (value) => String(value).padStart(2, '0')

const toDisplayValue = (value, fallback = '-') => {
  if (value == null) return fallback

  const textValue = String(value).trim()

  return textValue !== '' ? textValue : fallback
}

const toDateInstance = (value) => {
  if (!value) return null

  const nextDate = new Date(value)

  return isValidDate(nextDate) ? nextDate : null
}

const toDateKey = (value) => {
  const date = toDateInstance(value)

  if (!isValidDate(date)) return ''

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

const toTimeLabel = (value) => {
  const date = toDateInstance(value)

  if (!isValidDate(date)) return ''

  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

export const isWeekendDate = (value) => {
  const date = toDateInstance(value)

  if (!isValidDate(date)) return false

  const day = date.getDay()
  return day === 0 || day === 6
}

const getInterviewStageCode = (value) => {
  const stageCode = String(value || '').trim().toUpperCase()

  if (stageCode === 'USER') return 'USER'
  if (stageCode === 'HC') return 'HC'

  return ''
}

const getInterviewStageLabelKey = (stage) =>
  getInterviewStageCode(stage) === 'USER'
    ? 'interview_user'
    : 'interview_hc'

const getInterviewScheduleLabelKey = (stage) =>
  getInterviewStageCode(stage) === 'USER'
    ? 'interview_user_schedule'
    : 'interview_hc_schedule'

const getInterviewBookingLabelKey = (stage) =>
  getInterviewStageCode(stage) === 'USER'
    ? 'book_interview_user'
    : 'book_interview_hc'

const formatInterviewLocation = ({ location, room }) => {
  const rawLocation = String(location || '').trim()
  const rawRoom = String(room || '').trim()

  if (rawLocation && rawRoom) return `${rawLocation} - ${rawRoom}`
  if (rawLocation) return rawLocation
  if (rawRoom) return rawRoom

  return ''
}

const getActiveInterviewStage = (job, applicationStatus) => {
  const invitationStage = getInterviewStageCode(job?.interviewInvitationStage)

  if (invitationStage) return invitationStage
  if (applicationStatus === 'Interview User') return 'USER'
  if (applicationStatus === 'Interview HC') return 'HC'

  return ''
}

const buildEmptyInterviewSchedule = () => {
  const schedule = []
  const cursor = new Date()

  cursor.setHours(0, 0, 0, 0)

  let iterations = 0

  while (schedule.length < 5 && iterations < 30) {
    iterations += 1

    if (!isWeekendDate(cursor)) {
      const dateKey = toDateKey(cursor)

      schedule.push({
        date: `${dateKey}T00:00:00`,
        isDefault: false,
        slots: [],
      })
    }

    cursor.setDate(cursor.getDate() + 1)
  }

  return schedule
}

const buildInterviewAccessMeta = ({ lang, method, location, link }) => {
  const rawMethod = String(method || '').trim()
  const rawLocation = String(location || '').trim()
  const rawLink = String(link || '').trim()

  return {
    method: rawMethod || '-',
    methodNote:
      rawMethod.toLowerCase() === 'offline'
        ? Lang(lang, 'interview_offline_note')
        : '',
    link: rawLink,
    linkUrl: !rawLink
      ? ''
      : /^https?:\/\//i.test(rawLink)
        ? rawLink
        : `https://${rawLink}`,
    location: rawLocation || '-',
    mapUrl: rawLocation
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rawLocation)}`
      : '',
  }
}

const buildInterviewMeta = ({
  lang,
  stage,
  method,
  location,
  link,
  schedule = [],
  slotDateKeys = [],
  deadlineDate = '',
  isScheduled = false,
  selectedDateTime = '',
}) => {
  const rawMethod = String(method || '').trim()
  const rawLocation = String(location || '').trim()
  const rawLink = String(link || '').trim()
  const stageCode = getInterviewStageCode(stage)

  return {
    stage: stageCode || 'HC',
    stageLabelKey: getInterviewStageLabelKey(stageCode),
    scheduleLabelKey: getInterviewScheduleLabelKey(stageCode),
    bookingLabelKey: getInterviewBookingLabelKey(stageCode),
    isScheduled,
    deadlineDate,
    selectedDateTime,
    ...buildInterviewAccessMeta({
      lang,
      method: rawMethod,
      location: rawLocation,
      link: rawLink,
    }),
    schedule,
    slotDateKeys,
  }
}

export const formatLocalizedDate = (value, lang, options) => {
  const date = toDateInstance(value)

  if (!isValidDate(date)) return '-'

  const formattedDate = new Intl.DateTimeFormat(
    lang === 'id' ? 'id-ID' : 'en-US',
    options,
  ).format(date)

  return formattedDate
    ? formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
    : '-'
}

export const mapCandidateToMyApplicationOverview = (job, lang) => {
  const displayStatus = getNormalizedApplicationStatus(job)
  const progressSteps = buildApplicationProgressSteps(job, lang)
  const { badgeLabel, badgeClassName } = getApplicationStatusBadge(job, lang)
  const interviewStage = getActiveInterviewStage(job, displayStatus)
  const invitationStageCode = getInterviewStageCode(
    job?.interviewInvitationStage,
  )
  const hcLocation = String(job?.interviewLocation || job?.location || '').trim()
  const userLocation = String(
    job?.interviewUserLocation || job?.location || '',
  ).trim()
  const scheduledDate =
    interviewStage === 'USER' ? job?.interviewUserDate : job?.interviewDate
  const scheduledMethod =
    interviewStage === 'USER'
      ? job?.interviewUserMethod
      : job?.interviewMethod
  const scheduledLink =
    interviewStage === 'USER' ? job?.interviewUserLink : job?.interviewLink
  const scheduledLocation =
    interviewStage === 'USER' ? userLocation : hcLocation

  const overviewApplication = {
    id: `candidate-${job?.candidateID}`,
    kind: 'overview',
    candidateID: job?.candidateID || 0,
    jobID: job?.jobID || 0,
    title: toDisplayValue(job?.jobTitle),
    company: toDisplayValue(job?.companyName),
    email: job?.email || job?.Email || '',
    jobType: toDisplayValue(job?.jobType, ''),
    location: toDisplayValue(job?.location),
    dateApplied: job?.applicationDate || '',
    status: job?.status || '',
    displayStatus,
    rejectRemarks: toDisplayValue(
      job?.rejectRemarks ?? job?.RejectRemarks,
    ),
    isOffer: job?.isOffer,
    isInterview: job?.isInterview,
    isInterviewUser: job?.isInterviewUser,
    isMedicalCheckUp:
      job?.isMedicalCheckUp ?? job?.IsMedicalCheckUp,
    jobIsMedicalCheckUp:
      job?.jobIsMedicalCheckUp ?? job?.JobIsMedicalCheckUp,
    medicalCheckUpResult:
      job?.medicalCheckUpResult || job?.MedicalCheckUpResult || '',
    isAssessment: job?.isAssessment,
    isScreening: job?.isScreening,
    isScreeningUser: job?.isScreeningUser,
    isPassHardFilter: job?.isPassHardFilter ?? job?.IsPassHardFilter,
    isPassPretest: job?.isPassPretest ?? job?.IsPassPretest,
    hasPretest: job?.hasPretest ?? job?.HasPretest ?? false,
    pretestExpiredAt: job?.pretestExpiredAt || job?.PretestExpiredAt || null,
    pretestToken: job?.pretestToken || job?.PretestToken || '',
    progressSteps,
    assessmentDate: job?.assessmentDate || null,
    assessmentResult: job?.assessmentResult || job?.AssessmentResult || '',
    psychotestLink: job?.psychotestLink || '',
    assessmentTestLink:
      job?.assessmentTestLink || job?.AssessmentTestLink || '',
    interviewInvitationID: job?.interviewInvitationID || 0,
    interviewInvitationStage: invitationStageCode,
    interviewInvitationExpiredAt: job?.interviewInvitationExpiredAt || null,
    interviewInvitationStatus: job?.interviewInvitationStatus || '',
    interviewInvitationEmailStatus: job?.interviewInvitationEmailStatus || '',
    interviewInvitationIsReschedule:
      job?.interviewInvitationIsReschedule === true,
    hasActiveInterviewInvitation: job?.hasActiveInterviewInvitation === true,
    interviewStage,
    interviewDate: job?.interviewDate || null,
    interviewMethod: job?.interviewMethod || '',
    interviewLink: job?.interviewLink || '',
    interviewLocation: job?.interviewLocation || '',
    interviewUserDate: job?.interviewUserDate || null,
    interviewUserMethod: job?.interviewUserMethod || '',
    interviewUserLink: job?.interviewUserLink || '',
    interviewUserLocation: job?.interviewUserLocation || '',
    bookingMeta: {
      interviewInvitationID: job?.interviewInvitationID || 0,
      expiredAt: job?.interviewInvitationExpiredAt || '',
      isScheduled: false,
      isReschedule: job?.interviewInvitationIsReschedule === true,
      interviewStage: invitationStageCode || interviewStage,
    },
    badgeLabel,
    badgeClassName,
  }

  if (
    (displayStatus === 'Interview HC' ||
      displayStatus === 'Interview User') &&
    job?.interviewInvitationIsReschedule === true &&
    !scheduledDate
  ) {
    return {
      ...overviewApplication,
      kind: 'active',
      bookingMeta: {
        ...overviewApplication.bookingMeta,
        isReschedule: true,
      },
      interview: buildInterviewMeta({
        lang,
        stage: interviewStage,
        method: scheduledMethod,
        location: scheduledLocation,
        link: scheduledLink,
        schedule: buildEmptyInterviewSchedule(),
      }),
    }
  }

  if (
    (displayStatus === 'Interview HC' ||
      displayStatus === 'Interview User') &&
    scheduledDate
  ) {
    return {
      ...overviewApplication,
      kind: 'active',
      bookingMeta: {
        ...overviewApplication.bookingMeta,
        isScheduled: true,
      },
      interview: buildInterviewMeta({
        lang,
        stage: interviewStage,
        method: scheduledMethod,
        location: scheduledLocation,
        link: scheduledLink,
        isScheduled: true,
        selectedDateTime: scheduledDate,
      }),
    }
  }

  return overviewApplication
}

export const mapInterviewBookingToMyApplication = (
  response,
  lang,
  interviewInvitationID,
) => {
  const availableSlots = Array.isArray(response?.availableSlots)
    ? response.availableSlots
    : []
  const bookedSlots = Array.isArray(response?.bookedSlots)
    ? response.bookedSlots
    : []

  const interviewStage = getInterviewStageCode(response?.interviewStage)
  const interviewLocation = formatInterviewLocation({
    location: response?.location,
    room: response?.room,
  })
  const now = new Date()
  const todayDateKey = toDateKey(now)
  const slotDateKeys = Array.from(
    new Set(
      [...availableSlots, ...bookedSlots]
        .map((slotDay) => toDateKey(slotDay?.date))
        .filter((dateKey) => dateKey !== ''),
    ),
  ).sort()

  const availableSlotsByDate = new Map(
    availableSlots.map((slotDay) => {
      const dateKey = toDateKey(slotDay?.date)
      const slots = (Array.isArray(slotDay?.items) ? slotDay.items : [])
        .map((item) => {
          const interviewSlotID = Number(item?.interviewSlotID) || 0
          const start = toTimeLabel(item?.startDateTime)
          const end = toTimeLabel(item?.endDateTime)
          const startDateTime = item?.startDateTime || ''
          const endDateTime = item?.endDateTime || ''
          const parsedStartDateTime = toDateInstance(startDateTime)
          const slotLocation = formatInterviewLocation({
            location: item?.location,
            room: item?.room,
          })

          if (
            interviewSlotID <= 0 ||
            !start ||
            !end ||
            !isValidDate(parsedStartDateTime)
          ) {
            return null
          }

          if (
            toDateKey(startDateTime) === todayDateKey &&
            parsedStartDateTime.getTime() <= now.getTime()
          ) {
            return null
          }

          return {
            id: String(interviewSlotID),
            interviewSlotID,
            start,
            end,
            startDateTime,
            endDateTime,
            isFull: false,
            isRecommended: false,
            ...buildInterviewAccessMeta({
              lang,
              method: item?.method || response?.method,
              location: slotLocation || interviewLocation,
              link: item?.link || response?.link,
            }),
          }
        })
        .filter(Boolean)

      return [dateKey, slots]
    }),
  )

  const validFutureSlotDateKeys = slotDateKeys.filter(
    (key) => key >= todayDateKey && !isWeekendDate(key),
  )
  const latestSlotDateKey =
    validFutureSlotDateKeys.length > 0
      ? validFutureSlotDateKeys[validFutureSlotDateKeys.length - 1]
      : ''

  const schedule = []
  const cursor = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  )
  let workingDaysCount = 0
  let iterations = 0

  while (
    (workingDaysCount < 5 ||
      (latestSlotDateKey && toDateKey(cursor) <= latestSlotDateKey)) &&
    iterations < 60
  ) {
    iterations += 1

    if (!isWeekendDate(cursor)) {
      const dateKey = toDateKey(cursor)

      schedule.push({
        date: `${dateKey}T00:00:00`,
        isDefault: false,
        slots: availableSlotsByDate.get(dateKey) || [],
      })

      workingDaysCount += 1
    }

    cursor.setDate(cursor.getDate() + 1)
  }

  const firstAvailableDayIndex = schedule.findIndex((day) => day.slots.length > 0)

  if (firstAvailableDayIndex >= 0) {
    schedule[firstAvailableDayIndex] = {
      ...schedule[firstAvailableDayIndex],
      isDefault: true,
      slots: schedule[firstAvailableDayIndex].slots.map((slot, slotIndex) => ({
        ...slot,
        isRecommended: slotIndex === 0,
      })),
    }
  }

  return [
    {
      id: `my-application-${interviewInvitationID}`,
      kind: 'active',
      title: Lang(lang, getInterviewScheduleLabelKey(interviewStage)),
      company: toDisplayValue(response?.companyName),
      location: toDisplayValue(interviewLocation),
      status: '',
      displayStatus: interviewStage === 'USER' ? 'Interview User' : 'Interview HC',
      bookingMeta: {
        interviewInvitationID,
        expiredAt: response?.expiredAt || '',
        isScheduled: false,
        isReschedule: false,
        interviewStage,
      },
      interview: buildInterviewMeta({
        lang,
        stage: interviewStage,
        method: response?.method,
        location: interviewLocation,
        link: response?.link,
        schedule,
        slotDateKeys,
        deadlineDate: response?.expiredAt || '',
      }),
    },
  ]
}
